/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 29/03/22
 */

import React, { PropsWithChildren, useCallback, useState } from 'react';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { useGetRequest } from '@/utils/useStatefulRequests';

export enum FormType {
    ACTIVITY = 'ACTIVITY',
    LEAVE = 'LEAVE',
    PERSONAL_GOAL = 'PERSONAL_GOAL',
    HEALTH_AND_SAFETY_TRAINING = 'HEALTH_AND_SAFETY_TRAINING',
    CONSIDERATION = 'CONSIDERATION',
    BEHAVIOURAL = 'BEHAVIOURAL',
}

interface IFormsContext {
    error?: boolean;
    loading: boolean;
    // loadFormByType(type: FormType): Promise<void>;
    // getFormByType(type: FormType): FormPair | undefined;
    loadFormById(id: number): Promise<void>;
    getFormById(id: number): FormPair | undefined;
}

interface FormPair {
    id: number;
    schema: string;
    uischema: string;
}

type Forms = {
    [formId: number]: FormPair;
};

export const FormsContext = createUnprovisionedContext<IFormsContext>('FormsContext');

export const FormsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [forms, setForms] = useState<Forms>({});

    const { makeRequest, loading, error } = useGetRequest<FormPair>();

    const loadFormById = useCallback(async (id: number) => {
        try {
            const response = await makeRequest(`/forms/${id}`);
            setForms((oldForms) => ({
                ...oldForms,
                [id]: response.data,
            }));
        } catch (e) {
            //handled in hook
        }
    }, []);

    const getFormById = useCallback(
        (id: number): FormPair | undefined => {
            return forms[id];
        },
        [forms],
    );

    const context: IFormsContext = {
        loadFormById,
        getFormById,
        loading,
        error: !!error,
    };

    return <FormsContext.Provider value={context}>{children}</FormsContext.Provider>;
};

export default FormsProvider;
